/**
 * Owner Name Matching & Family Grouping Utilities
 * 
 * Algorithms for detecting:
 * - Same owner with different name formats (e.g., "John A Smith" vs "John Smith")
 * - Family members (same last name)
 * - Corporate/trust entities
 */

import stringSimilarity from 'string-similarity';

// ============================================
// Types
// ============================================

export interface OwnerInfo {
  rawName: string;
  normalizedName: string;
  lastName: string;
  firstName?: string;
  middleName?: string;
  suffix?: string;
  isEntity: boolean;  // Corporation, LLC, Trust, etc.
  entityType?: 'corporation' | 'llc' | 'trust' | 'estate' | 'partnership' | 'family' | 'government';
}

export interface OwnerCluster {
  id: string;
  clusterType: 'same_person' | 'family' | 'entity';
  representativeName: string;
  members: OwnerInfo[];
  totalParcels: number;
  totalAcres: number;
  totalValue: number;
  color: string;  // Assigned color for map visualization
}

export interface ParcelOwnerData {
  parcelId: string;
  ownerName: string;
  acres: number;
  value: number;
  tier?: number;
}

// ============================================
// Constants
// ============================================

const ENTITY_KEYWORDS = [
  'LLC', 'L.L.C.', 'INC', 'INCORPORATED', 'CORP', 'CORPORATION',
  'TRUST', 'LIVING TRUST', 'FAMILY TRUST', 'IRREVOCABLE TRUST',
  'ESTATE', 'ESTATE OF', 'HEIRS', 'HEIRS OF',
  'LP', 'L.P.', 'LLP', 'L.L.P.', 'PARTNERSHIP',
  'FAMILY', 'REVOCABLE', 'IRREVOCABLE',
  'COUNTY', 'STATE', 'CITY', 'TOWN', 'VILLAGE', 'GOVERNMENT',
  'CHURCH', 'BAPTIST', 'METHODIST', 'PRESBYTERIAN',
  'UNIFIED', 'ASSOCIATES', 'HOLDINGS', 'PROPERTIES', 'INVESTMENTS',
];

const SUFFIXES = ['JR', 'SR', 'II', 'III', 'IV', 'V', 'ESQ', 'MD', 'PHD'];

// Colors for visualization (distinct, colorblind-friendly palette)
const CLUSTER_COLORS = [
  '#E63946', // red
  '#457B9D', // blue
  '#2A9D8F', // teal
  '#E9C46A', // yellow
  '#F4A261', // orange
  '#9B5DE5', // purple
  '#00BBF9', // cyan
  '#00F5D4', // mint
  '#FEE440', // bright yellow
  '#9B2226', // dark red
  '#005F73', // dark teal
  '#94D2BD', // light teal
  '#EE9B00', // gold
  '#CA6702', // rust
  '#BB3E03', // burnt orange
];

// ============================================
// Name Parsing
// ============================================

/**
 * Normalize a name for comparison
 * - Uppercase
 * - Remove extra whitespace
 * - Remove punctuation except hyphens
 */
function normalizeName(name: string): string {
  return name
    .toUpperCase()
    .replace(/[.,'"]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Check if the name represents a legal entity (not a person)
 */
function isEntityName(name: string): { isEntity: boolean; entityType?: OwnerInfo['entityType'] } {
  const upper = name.toUpperCase();
  
  for (const keyword of ENTITY_KEYWORDS) {
    if (upper.includes(keyword)) {
      // Determine type
      if (upper.includes('TRUST') || upper.includes('REVOCABLE') || upper.includes('IRREVOCABLE')) {
        return { isEntity: true, entityType: 'trust' };
      }
      if (upper.includes('ESTATE') || upper.includes('HEIRS')) {
        return { isEntity: true, entityType: 'estate' };
      }
      if (upper.includes('LLC') || upper.includes('L.L.C.')) {
        return { isEntity: true, entityType: 'llc' };
      }
      if (upper.includes('INC') || upper.includes('CORP')) {
        return { isEntity: true, entityType: 'corporation' };
      }
      if (upper.includes('LP') || upper.includes('L.P.') || upper.includes('PARTNERSHIP')) {
        return { isEntity: true, entityType: 'partnership' };
      }
      if (upper.includes('FAMILY') || upper.includes('UNIFIED')) {
        return { isEntity: true, entityType: 'family' };
      }
      if (upper.includes('COUNTY') || upper.includes('STATE') || upper.includes('CITY') || upper.includes('GOVERNMENT')) {
        return { isEntity: true, entityType: 'government' };
      }
      return { isEntity: true };
    }
  }
  
  return { isEntity: false };
}

/**
 * Extract last name from a person's name
 * Handles formats like:
 * - "SMITH JOHN A"
 * - "JOHN A SMITH"
 * - "SMITH, JOHN A"
 */
function extractLastName(name: string): string {
  const normalized = normalizeName(name);
  const parts = normalized.split(/[\s,]+/).filter(p => p.length > 0);
  
  if (parts.length === 0) return '';
  
  // Remove suffixes
  const filteredParts = parts.filter(p => !SUFFIXES.includes(p));
  if (filteredParts.length === 0) return parts[0];
  
  // If comma present, first part is likely the last name (e.g., "Smith, John")
  if (normalized.includes(',')) {
    return filteredParts[0];
  }
  
  // Check if first word looks like a last name (NC records often do "LASTNAME FIRSTNAME")
  // Heuristic: if first word is longer and second word is shorter, first is likely lastname
  if (filteredParts.length >= 2) {
    const first = filteredParts[0];
    const second = filteredParts[1];
    
    // Common first names that might look like last names
    const commonFirstNames = ['JOHN', 'JAMES', 'ROBERT', 'MICHAEL', 'WILLIAM', 'DAVID', 'RICHARD', 'THOMAS', 'MARY', 'PATRICIA', 'JENNIFER', 'LINDA', 'ELIZABETH', 'BARBARA', 'SUSAN'];
    
    if (commonFirstNames.includes(first) && !commonFirstNames.includes(second)) {
      // First word is a common first name, so second is likely last name
      return filteredParts[filteredParts.length - 1];
    }
    
    // Default: assume NC format (LASTNAME FIRSTNAME)
    return first;
  }
  
  return filteredParts[0];
}

/**
 * Parse a name into structured components
 */
export function parseOwnerName(rawName: string): OwnerInfo {
  const normalized = normalizeName(rawName);
  const { isEntity, entityType } = isEntityName(normalized);
  
  const result: OwnerInfo = {
    rawName,
    normalizedName: normalized,
    lastName: '',
    isEntity,
    entityType,
  };
  
  if (isEntity) {
    // For entities, use the full name as "last name" for grouping
    result.lastName = normalized;
  } else {
    result.lastName = extractLastName(normalized);
    
    // Try to extract first name
    const parts = normalized.split(/[\s,]+/).filter(p => p.length > 0 && !SUFFIXES.includes(p));
    if (parts.length >= 2) {
      // If comma format, second part is first name
      if (rawName.includes(',')) {
        result.firstName = parts[1];
        if (parts.length >= 3) result.middleName = parts.slice(2).join(' ');
      } else {
        // NC format: LASTNAME FIRSTNAME MIDDLE
        result.firstName = parts[1];
        if (parts.length >= 3) result.middleName = parts.slice(2).join(' ');
      }
    }
    
    // Check for suffix
    const allParts = normalized.split(/[\s,]+/);
    for (const suffix of SUFFIXES) {
      if (allParts.includes(suffix)) {
        result.suffix = suffix;
        break;
      }
    }
  }
  
  return result;
}

// ============================================
// Similarity & Clustering
// ============================================

/**
 * Calculate similarity between two owner names
 * Returns a score from 0 to 1
 */
export function calculateOwnerSimilarity(name1: OwnerInfo, name2: OwnerInfo): number {
  // Exact match
  if (name1.normalizedName === name2.normalizedName) return 1.0;
  
  // If both are entities, compare full names
  if (name1.isEntity && name2.isEntity) {
    return stringSimilarity.compareTwoStrings(name1.normalizedName, name2.normalizedName);
  }
  
  // If one is entity and one is person, low similarity
  if (name1.isEntity !== name2.isEntity) return 0.1;
  
  // Both are people - compare last names first
  if (name1.lastName !== name2.lastName) {
    // Check if last names are similar (typos, hyphenation)
    const lastNameSim = stringSimilarity.compareTwoStrings(name1.lastName, name2.lastName);
    if (lastNameSim < 0.85) return lastNameSim * 0.3; // Different families
  }
  
  // Same last name - now compare first names
  if (name1.firstName && name2.firstName) {
    // Exact first name match
    if (name1.firstName === name2.firstName) {
      // Check middle names/initials
      if (name1.middleName && name2.middleName) {
        // Full middle names match
        if (name1.middleName === name2.middleName) return 0.98;
        // One is initial of the other
        if (name1.middleName[0] === name2.middleName[0]) return 0.95;
        // Different middle names - might be different people
        return 0.7;
      }
      // One has middle, one doesn't
      return 0.9;
    }
    
    // Check if one first name is initial of the other
    if (name1.firstName.length === 1 && name2.firstName.startsWith(name1.firstName)) return 0.85;
    if (name2.firstName.length === 1 && name1.firstName.startsWith(name2.firstName)) return 0.85;
    
    // Different first names, same last name = family
    return 0.6;
  }
  
  // Same last name but missing first name info
  return 0.7;
}

/**
 * Group owners into clusters based on similarity
 * Uses a union-find / agglomerative approach
 */
export function clusterOwners(parcels: ParcelOwnerData[], similarityThreshold = 0.85): OwnerCluster[] {
  const ownerInfos = parcels.map(p => ({
    parcel: p,
    info: parseOwnerName(p.ownerName),
  }));
  
  // Group by last name first for efficiency
  const byLastName: Map<string, typeof ownerInfos> = new Map();
  
  for (const item of ownerInfos) {
    const lastName = item.info.lastName || 'UNKNOWN';
    if (!byLastName.has(lastName)) {
      byLastName.set(lastName, []);
    }
    byLastName.get(lastName)!.push(item);
  }
  
  const clusters: OwnerCluster[] = [];
  let colorIndex = 0;
  
  for (const [lastName, items] of byLastName) {
    // Within each last name group, cluster by similarity
    const processed = new Set<number>();
    
    for (let i = 0; i < items.length; i++) {
      if (processed.has(i)) continue;
      
      const cluster: typeof items = [items[i]];
      processed.add(i);
      
      // Find all similar items
      for (let j = i + 1; j < items.length; j++) {
        if (processed.has(j)) continue;
        
        const sim = calculateOwnerSimilarity(items[i].info, items[j].info);
        if (sim >= similarityThreshold) {
          cluster.push(items[j]);
          processed.add(j);
        }
      }
      
      // Determine cluster type
      let clusterType: OwnerCluster['clusterType'] = 'same_person';
      
      // Check if all entries are the same person or if it's a family
      const uniqueFirstNames = new Set(
        cluster
          .filter(c => !c.info.isEntity && c.info.firstName)
          .map(c => c.info.firstName)
      );
      
      if (items[0].info.isEntity) {
        clusterType = 'entity';
      } else if (uniqueFirstNames.size > 1) {
        clusterType = 'family';
      }
      
      // Calculate totals
      const totalParcels = cluster.length;
      const totalAcres = cluster.reduce((sum, c) => sum + c.parcel.acres, 0);
      const totalValue = cluster.reduce((sum, c) => sum + c.parcel.value, 0);
      
      // Choose representative name (most common or longest)
      const nameCounts: Map<string, number> = new Map();
      for (const c of cluster) {
        const name = c.info.normalizedName;
        nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
      }
      let representativeName = '';
      let maxCount = 0;
      for (const [name, count] of nameCounts) {
        if (count > maxCount || (count === maxCount && name.length > representativeName.length)) {
          representativeName = name;
          maxCount = count;
        }
      }
      
      clusters.push({
        id: `${lastName}-${colorIndex}`,
        clusterType,
        representativeName,
        members: cluster.map(c => c.info),
        totalParcels,
        totalAcres,
        totalValue,
        color: CLUSTER_COLORS[colorIndex % CLUSTER_COLORS.length],
      });
      
      colorIndex++;
    }
  }
  
  // Sort clusters by total acres descending
  clusters.sort((a, b) => b.totalAcres - a.totalAcres);
  
  return clusters;
}

/**
 * Group clusters into families (by last name)
 */
export function groupClustersByFamily(clusters: OwnerCluster[]): Map<string, OwnerCluster[]> {
  const families: Map<string, OwnerCluster[]> = new Map();
  
  for (const cluster of clusters) {
    const lastName = cluster.members[0]?.lastName || 'UNKNOWN';
    if (!families.has(lastName)) {
      families.set(lastName, []);
    }
    families.get(lastName)!.push(cluster);
  }
  
  return families;
}

/**
 * Get owner-to-color mapping for map visualization
 */
export function getOwnerColorMap(clusters: OwnerCluster[]): Map<string, string> {
  const colorMap: Map<string, string> = new Map();
  
  for (const cluster of clusters) {
    for (const member of cluster.members) {
      colorMap.set(member.rawName, cluster.color);
      colorMap.set(member.normalizedName, cluster.color);
    }
  }
  
  return colorMap;
}

// ============================================
// Statistics
// ============================================

export interface OwnershipStats {
  totalClusters: number;
  uniqueIndividuals: number;
  familyGroups: number;
  entityCount: number;
  topOwners: Array<{
    name: string;
    clusterType: OwnerCluster['clusterType'];
    parcels: number;
    acres: number;
    value: number;
    percentOfArea: number;
    color: string;
  }>;
  familyBreakdown: Array<{
    lastName: string;
    memberCount: number;
    totalParcels: number;
    totalAcres: number;
    totalValue: number;
    percentOfArea: number;
  }>;
}

export function calculateOwnershipStats(clusters: OwnerCluster[], totalNeighborAcres: number): OwnershipStats {
  const families = groupClustersByFamily(clusters);
  
  return {
    totalClusters: clusters.length,
    uniqueIndividuals: clusters.filter(c => c.clusterType === 'same_person').length,
    familyGroups: clusters.filter(c => c.clusterType === 'family').length,
    entityCount: clusters.filter(c => c.clusterType === 'entity').length,
    topOwners: clusters.slice(0, 10).map(c => ({
      name: c.representativeName,
      clusterType: c.clusterType,
      parcels: c.totalParcels,
      acres: c.totalAcres,
      value: c.totalValue,
      percentOfArea: totalNeighborAcres > 0 ? (c.totalAcres / totalNeighborAcres) * 100 : 0,
      color: c.color,
    })),
    familyBreakdown: Array.from(families.entries())
      .map(([lastName, familyClusters]) => {
        const totalParcels = familyClusters.reduce((sum, c) => sum + c.totalParcels, 0);
        const totalAcres = familyClusters.reduce((sum, c) => sum + c.totalAcres, 0);
        const totalValue = familyClusters.reduce((sum, c) => sum + c.totalValue, 0);
        
        return {
          lastName,
          memberCount: familyClusters.length,
          totalParcels,
          totalAcres,
          totalValue,
          percentOfArea: totalNeighborAcres > 0 ? (totalAcres / totalNeighborAcres) * 100 : 0,
        };
      })
      .sort((a, b) => b.totalAcres - a.totalAcres)
      .slice(0, 10),
  };
}

